import { useCallback } from 'react';
import { regionsApi } from '../api/regions.api';
import type { Region, RegionSelector } from '../types/regions.types';
import { HierarchyInput } from '@/shared/components/ahkinia/HierarchyInput';
import type {
  HierarchyFetchParams,
  HierarchyInputProps,
} from '@/shared/components/ahkinia/HierarchyInput';

/**
 * Shared prefix for every query key HierarchyInput builds for this picker
 * (`{prefix}{countryIds}-{selector}` + 'items'|'path'|'children-check' suffixes).
 * Exported so region mutations can invalidate all of this picker's cached
 * country/selector variants via a predicate match, without needing to know
 * which exact combination is currently open in the UI.
 */
export const REGION_HIERARCHY_QUERY_PREFIX = 'region-hierarchy-';

interface RegionHierarchyInputProps extends Omit<
  HierarchyInputProps<Region>,
  'fetchItems' | 'fetchItemById' | 'getLabel' | 'getParent' | 'queryKey'
> {
  countryIds: string[];
  selector: RegionSelector;
  /** Id to exclude from the candidate list — pass the region being edited so it can't become its own parent. */
  excludeId?: string;
}

export function RegionHierarchyInput({
  countryIds,
  selector,
  excludeId,
  ...props
}: RegionHierarchyInputProps) {
  const countryIdsKey = countryIds.join(',');
  const fetchItems = useCallback(
    async ({ parentId, page = 1, pageSize = 100 }: HierarchyFetchParams) => {
      const result = await regionsApi.getRegions({
        countryIds,
        selector,
        parentId,
        page,
        pageSize,
      });
      const items = result.data?.items ?? [];
      return {
        items: excludeId ? items.filter((item) => item.id !== excludeId) : items,
        hasMore: result.data?.hasNextPage ?? false,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- countryIds is a plain array re-created by callers on every render; countryIdsKey (its joined value) is the real identity for memoization here.
    [countryIdsKey, selector, excludeId],
  );

  const fetchItemById = useCallback(async (id: string) => {
    const result = await regionsApi.getRegionById(id);
    return result.data as Region;
  }, []);

  return (
    <HierarchyInput<Region>
      {...props}
      fetchItems={fetchItems}
      fetchItemById={fetchItemById}
      getLabel={(r) => r.name}
      getParent={(r) => r.parentId}
      queryKey={`${REGION_HIERARCHY_QUERY_PREFIX}${countryIdsKey}-${selector}`}
    />
  );
}
